
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Equipment, 
  EquipmentType, 
  Frequency,
  InspectionPlan,
  InspectionActual,
  Finding
} from '../types';
import { AreaConfig, FirebaseConfig } from '../App';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2,
  X,
  AlertTriangle,
  PlusCircle,
  Building2,
  Layers,
  ClipboardList,
  CheckCircle2,
  Database,
  Server,
  Zap,
  Flame,
  CloudUpload,
  Info,
  Check,
  ChevronRight,
  MapPin,
  Rocket,
  QrCode,
  Printer
} from 'lucide-react';
import { EQUIPMENT_ICONS } from '../constants';

const DEFAULT_CHECKLISTS: Record<EquipmentType, string[]> = {
  [EquipmentType.APAR]: [
    'Tekanan (Pressure Gauge di Zona Hijau)', 
    'Segel & Pin Pengaman Masih Utuh', 
    'Kondisi Tabung (Tidak Berkarat/Keropos)', 
    'Selang & Nozzle (Tidak Retak/Tersumbat)', 
    'Label Instruksi/Exp Date Masih Jelas', 
    'Hanger/Bracket Kokoh',
    'Akses Menuju Alat Tidak Terhalang'
  ],
  [EquipmentType.HYDRANT]: [
    'Kondisi Main Valve (Mudah Diputar)', 
    'Kondisi Hose/Selang (Tidak Bocor/Rapuh)', 
    'Coupling & Seal (Karet Tidak Pecah)', 
    'Nozzle (Bisa Diatur Spray/Jet)', 
    'Kondisi Box (Cat Bagus & Engsel Lancar)', 
    'Tekanan Air Statis (Cek Pressure)',
    'Akses Pilar/Box Bebas Hambatan'
  ],
  [EquipmentType.EYEWASH]: [
    'Debit Air Stabil & Simetris', 
    'Kebersihan Bak & Nozzle', 
    'Dust Cover Otomatis Terlepas saat Aktivasi', 
    'Push Lever/Pedal Lancar', 
    'Drainase/Pembuangan Air Lancar',
    'Label Petunjuk Penggunaan Ada'
  ],
  [EquipmentType.SHOWER]: [
    'Flow Rate Air (Minimum 75L/menit)', 
    'Pull Lever/Handle Berfungsi Sekali Tarik', 
    'Area Sekitar Bersih & Tidak Licin', 
    'Warna Air Bersih (Tidak Berkarat)',
    'Signage Darurat Terpampang Jelas'
  ],
  [EquipmentType.DETECTOR]: [
    'Indikator Power (Steady On)', 
    'Uji Fungsi Respon Sensor (Smoke/Heat/Gas)', 
    'Audible & Visible Alarm Berfungsi', 
    'Kabel & Koneksi Terminal Kencang',
    'Status di Control Panel (MCFA) Normal',
    'Masa Kalibrasi Valid (Gas Detector)'
  ],
  [EquipmentType.LAMP]: [
    'Uji Test Button (Lampu Menyala)', 
    'Kondisi Baterai/Accu (Tidak Kembung)', 
    'Brightness (Kecerahan Sesuai Standar)', 
    'Fisik Casing & Mounting Kokoh',
    'Arah Sorot Lampu ke Jalur Evakuasi'
  ],
  [EquipmentType.PPE]: [
    'Kondisi Fisik (Tidak Sobek/Retak)', 
    'Kebersihan Alat (Sanitized)', 
    'Masa Kadaluwarsa (Expired Date)', 
    'Kelengkapan Komponen (Tali/Lensa)',
    'Penyimpanan di Box/Lemari Standar'
  ],
  [EquipmentType.FIRST_AID]: [
    'Kuantitas Isi Sesuai Daftar Lampiran', 
    'Masa Kadaluwarsa Obat/Kasa', 
    'Kebersihan & Kerapian Dalam Kotak', 
    'Mudah Diakses & Signage Terlihat',
    'Pencatatan Log Penggunaan P3K'
  ],
  [EquipmentType.SPILL_KIT]: [
    'Kuantitas Absorbent Pads/Pillows', 
    'Ketersediaan Kantong Limbah B3', 
    'Kondisi Container/Bin (Tertutup Rapat)', 
    'APD Darurat (Goggle, Apron, Sarung Tangan)',
    'Instruksi Penanganan Tumpahan Ada'
  ],
  [EquipmentType.STRETCHER]: [
    'Frame/Rangka Tidak Bengkok/Patah', 
    'Sabuk Pengaman (Buckle & Strap) Kuat', 
    'Kondisi Roda/Handle Lipat Lancar', 
    'Kebersihan Permukaan Tandu',
    'Lokasi Penempatan Sesuai Markah'
  ],
  [EquipmentType.FIRE_BLANKET]: [
    'Casing/Bag Kondisi Baik (Tidak Sobek)', 
    'Tali Penarik (Pull Straps) Terlihat Jelas', 
    'Kain Serat Kaca Tidak Rontok/Rapuh', 
    'Instruksi Pelipatan Masih Terbaca',
    'Mudah Diambil dalam Sekali Tarik'
  ],
};

const AREA_SHORT_MAP: Record<string, string> = {
  'Plant 1 - Production': 'P1',
  'Plant 2 - Warehouse': 'P2',
  'Gedung Office': 'OFF'
};

interface MasterDataProps {
  equipments: Equipment[];
  areaConfigs: AreaConfig[];
  firebaseConfig: FirebaseConfig;
  updateFirebaseConfig: (config: FirebaseConfig) => void;
  pushAllToFirebase: () => void;
  syncError?: string | null;
  addEquipment: (eq: Equipment) => void;
  addEquipmentsBulk: (eqs: Equipment[]) => void;
  updateEquipment: (eq: Equipment) => void;
  deleteEquipment: (id: string) => void;
  addArea: (name: string) => void;
  deleteArea: (id: string) => void;
  addSection: (areaId: string, sectionName: string) => void;
  deleteSection: (areaId: string, sectionName: string) => void;
  plans: InspectionPlan[];
  actuals: InspectionActual[];
  findings: Finding[];
  resetLocalDatabase: () => void;
}

const MasterData: React.FC<MasterDataProps> = ({ 
  equipments, 
  areaConfigs, 
  firebaseConfig,
  updateFirebaseConfig,
  pushAllToFirebase,
  syncError,
  addEquipment, 
  addEquipmentsBulk,
  updateEquipment, 
  deleteEquipment,
  addArea,
  deleteArea,
  addSection,
  deleteSection,
  plans,
  actuals,
  findings,
  resetLocalDatabase
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'assets' | 'areas' | 'firebase' | 'database'>('assets');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEq, setEditingEq] = useState<Equipment | null>(null);
  const [newAreaName, setNewAreaName] = useState('');
  const [newSectionNames, setNewSectionNames] = useState<Record<string, string>>({});
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [showLabel, setShowLabel] = useState<Equipment | null>(null);

  const [formData, setFormData] = useState<Partial<Equipment>>({
    type: EquipmentType.APAR,
    frequency: Frequency.MONTHLY,
    code: '',
    location: '',
    area: areaConfigs[0]?.name || '',
    section: '',
    technicalChecklist: []
  });

  const availableSections = useMemo(() => {
    return areaConfigs.find(a => a.name === formData.area)?.sections.map(s => s.name) || [];
  }, [formData.area, areaConfigs]);

  const generateNewCode = (type: EquipmentType, area: string) => {
    const areaCode = AREA_SHORT_MAP[area] || area.substring(0, 3).toUpperCase();
    const typePrefix = type.split(' ')[0].toUpperCase();
    
    const existingCodes = equipments
      .filter(e => e.type === type && e.area === area)
      .map(e => {
        const parts = e.code.split('-');
        return parseInt(parts[parts.length - 1]) || 0;
      });
    
    const nextNum = existingCodes.length > 0 ? Math.max(...existingCodes) + 1 : 1;
    return `${typePrefix}-${areaCode}-${nextNum.toString().padStart(3, '0')}`;
  };

  useEffect(() => {
    if (!editingEq && formData.type && formData.area) {
      const newCode = generateNewCode(formData.type, formData.area);
      setFormData(prev => ({ ...prev, code: newCode }));
    }
  }, [formData.type, formData.area, editingEq]);

  const handleSeedDummy = async () => {
    const confirm = window.confirm("Hasilkan 30+ aset safety profesional sekarang?");
    if (!confirm) return;

    const dummyData: Equipment[] = [];
    const types = Object.values(EquipmentType);
    
    areaConfigs.forEach(area => {
        types.forEach(type => {
            const count = type === EquipmentType.APAR ? 4 : 1;
            for(let i=0; i < count; i++) {
                const code = generateNewCode(type, area.name);
                dummyData.push({
                    id: `seed-${code}-${Date.now()}-${Math.random()}`,
                    code,
                    type,
                    frequency: Frequency.MONTHLY,
                    area: area.name,
                    section: area.sections[0]?.name || 'Assembly',
                    location: `Lokasi Strategis ${i+1}`,
                    technicalChecklist: DEFAULT_CHECKLISTS[type]
                });
            }
        });
    });

    await addEquipmentsBulk(dummyData);
    alert("30+ Aset Master Berhasil Dihasilkan!");
  };

  const filteredEquipments = equipments.filter(eq => {
    const searchStr = `${eq.code} ${eq.type} ${eq.location} ${eq.area} ${eq.section}`.toLowerCase();
    return searchStr.includes(searchTerm.toLowerCase());
  });

  const handleOpenAdd = () => {
    setEditingEq(null);
    const initialArea = areaConfigs[0]?.name || '';
    const initialType = EquipmentType.APAR;
    setFormData({
      type: initialType,
      frequency: Frequency.MONTHLY,
      code: generateNewCode(initialType, initialArea),
      location: '',
      area: initialArea,
      section: '',
      technicalChecklist: DEFAULT_CHECKLISTS[initialType]
    });
    setShowForm(true);
  };

  const handleTypeChange = (type: EquipmentType) => {
    setFormData(prev => ({
      ...prev,
      type,
      code: generateNewCode(type, prev.area || ''),
      technicalChecklist: editingEq ? prev.technicalChecklist : DEFAULT_CHECKLISTS[type]
    }));
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setFormData(prev => ({
        ...prev,
        technicalChecklist: [...(prev.technicalChecklist || []), newChecklistItem.trim()]
      }));
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      technicalChecklist: (prev.technicalChecklist || []).filter((_, i) => i !== index)
    }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.code || !formData.area || !formData.section) {
      alert("Lengkapi data!");
      return;
    }
    const finalData: Equipment = {
      ...formData as Equipment,
      id: editingEq ? editingEq.id : `eq-${Date.now()}`
    };
    if (editingEq) updateEquipment(finalData);
    else addEquipment(finalData);
    setShowForm(false);
  };

  const handleAddArea = (e: React.FormEvent) => {
    e.preventDefault();
    if (newAreaName.trim()) { addArea(newAreaName.trim()); setNewAreaName(''); }
  };

  const handleAddSection = (areaId: string) => {
    const name = newSectionNames[areaId];
    if (name && name.trim()) {
      addSection(areaId, name.trim());
      setNewSectionNames(prev => ({ ...prev, [areaId]: '' }));
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">Master Repository</h1>
          <p className="text-slate-500 mt-1 font-medium italic">Database aset, lokasi, dan infrastruktur cloud.</p>
        </div>
        <div className="flex bg-white p-1 rounded-2xl border border-slate-100 shadow-sm overflow-x-auto no-print">
            <button onClick={() => setActiveSubTab('assets')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${activeSubTab === 'assets' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400'}`}>Assets</button>
            <button onClick={() => setActiveSubTab('areas')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${activeSubTab === 'areas' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400'}`}>Locations</button>
            <button onClick={() => setActiveSubTab('firebase')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${activeSubTab === 'firebase' ? 'bg-amber-500 text-white shadow-lg' : 'text-slate-400'}`}>Firebase Cloud</button>
            <button onClick={() => setActiveSubTab('database')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${activeSubTab === 'database' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400'}`}>Local Database</button>
        </div>
      </div>

      {activeSubTab === 'assets' && (
        <>
            <div className="flex flex-col lg:flex-row gap-4 bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm no-print">
                <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input type="text" placeholder="Cari Kode Alat, Jenis, atau Area..." className="w-full pl-12 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-emerald-500/10 text-sm font-bold outline-none transition-all" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleSeedDummy} className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:bg-indigo-700 flex items-center gap-2 group transition-all">
                      <Rocket className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform" /> Mass Generate
                  </button>
                  <button onClick={pushAllToFirebase} className="bg-slate-900 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:bg-slate-800 flex items-center gap-2 group">
                      <CloudUpload className="w-4 h-4 text-emerald-400 group-hover:scale-110 transition-transform" /> Push All to Cloud
                  </button>
                  <button onClick={handleOpenAdd} className="bg-emerald-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-emerald-600/20 hover:bg-emerald-700 flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Register Asset
                  </button>
                </div>
            </div>
            <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden overflow-x-auto no-print">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-gray-100">
                        <tr>
                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">QR</th>
                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Type & Category</th>
                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">ID Code</th>
                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Gedung / Seksi</th>
                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredEquipments.length > 0 ? filteredEquipments.map((eq) => (
                            <tr key={eq.id} className="hover:bg-slate-50/50 transition-colors group">
                                <td className="px-8 py-5 text-center">
                                    <button onClick={() => setShowLabel(eq)} className="p-2 bg-slate-50 border border-slate-100 rounded-xl hover:bg-white transition-all">
                                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=50x50&data=${eq.code}`} className="w-8 h-8 opacity-60 group-hover:opacity-100" alt="qr" />
                                    </button>
                                </td>
                                <td className="px-8 py-5 font-black text-slate-800 text-sm">{eq.type}</td>
                                <td className="px-8 py-5 font-mono text-xs font-black text-indigo-600 bg-indigo-50/50 px-4 py-2 rounded-xl w-fit">{eq.code}</td>
                                <td className="px-8 py-5"><div className="flex flex-col gap-1"><span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-lg w-fit">{eq.area}</span><span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter pl-1">{eq.section}</span></div></td>
                                <td className="px-8 py-5 text-right"><div className="flex items-center justify-end gap-1"><button onClick={() => { setEditingEq(eq); setFormData(eq); setShowForm(true); }} className="p-2 text-slate-300 hover:text-emerald-600"><Edit2 className="w-4 h-4" /></button><button onClick={() => deleteEquipment(eq.id)} className="p-2 text-slate-300 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button></div></td>
                            </tr>
                        )) : (
                          <tr>
                            <td colSpan={5} className="py-20 text-center text-slate-400 italic">Database Kosong. Gunakan tombol 'Mass Generate'.</td>
                          </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
      )}

      {activeSubTab === 'areas' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 no-print">
            <div className="lg:col-span-8 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {areaConfigs.map(area => (
                        <div key={area.id} className="bg-white rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col group hover:border-emerald-500 transition-all">
                            <div className="p-8 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                                <div className="flex items-center gap-4"><div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-emerald-500 group-hover:rotate-12 transition-transform"><Building2 className="w-6 h-6" /></div><h3 className="text-lg font-black text-slate-900 uppercase tracking-tighter">{area.name}</h3></div>
                                <button onClick={() => deleteArea(area.id)} className="p-3 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-2xl transition-all"><Trash2 className="w-5 h-5" /></button>
                            </div>
                            <div className="p-8 flex-1 space-y-6">
                                <div><h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-4 flex items-center gap-2"><Layers className="w-3.5 h-3.5" /> Seksi / Bagian</h4><div className="flex flex-wrap gap-2">{area.sections.map(section => (<div key={section.name} className="flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-700">{section.name}<button onClick={() => deleteSection(area.id, section.name)} className="text-slate-300 hover:text-rose-600 transition-colors"><X className="w-3 h-3" /></button></div>))}</div></div>
                                <div className="pt-4 border-t border-slate-100 flex gap-2"><input type="text" placeholder="Seksi Baru..." value={newSectionNames[area.id] || ''} onChange={e => setNewSectionNames({...newSectionNames, [area.id]: e.target.value})} className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none" onKeyPress={e => e.key === 'Enter' && handleAddSection(area.id)} /><button onClick={() => handleAddSection(area.id)} className="p-3 bg-emerald-600 text-white rounded-xl"><PlusCircle className="w-5 h-5" /></button></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="lg:col-span-4"><div className="bg-slate-900 p-10 rounded-[3rem] shadow-2xl text-white"><h3 className="text-xl font-black uppercase tracking-tighter mb-4 flex items-center gap-3"><Plus className="w-6 h-6 text-emerald-400" /> Tambah Gedung</h3><form onSubmit={handleAddArea} className="space-y-4 mt-6"><input required type="text" value={newAreaName} onChange={e => setNewAreaName(e.target.value)} placeholder="Nama Gedung / Plant" className="w-full bg-white/10 border border-white/5 rounded-2xl px-6 py-4 text-sm font-bold outline-none text-white placeholder:text-white/20" /><button type="submit" className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-emerald-600/20">Simpan Area Baru</button></form></div></div>
        </div>
      )}

      {activeSubTab === 'firebase' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 animate-in slide-in-from-right-12 duration-500 no-print">
            <div className="lg:col-span-7 space-y-8">
                <div className="bg-white p-12 rounded-[4rem] border border-amber-100 shadow-sm space-y-10">
                    <div className="flex items-start gap-6"><div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-[2rem] flex items-center justify-center shrink-0"><Server className="w-8 h-8" /></div><div><h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">Centralized Cloud Sync</h3><p className="text-slate-500 font-medium italic mt-1 leading-relaxed">Kelola sinkronisasi database antara lokal dan Firestore.</p></div></div>
                    
                    {syncError && (
                      <div className="p-6 bg-rose-50 border border-rose-100 rounded-[2rem] flex items-start gap-4">
                        <AlertTriangle className="w-6 h-6 text-rose-500 shrink-0" />
                        <div><p className="text-xs font-black text-rose-900 uppercase">Koneksi Bermasalah</p><p className="text-[11px] text-rose-600 font-bold mt-1 leading-relaxed">{syncError}</p></div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6">
                        <div className="space-y-2"><label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Project ID</label><input type="text" placeholder="my-safety-project" value={firebaseConfig.projectId} onChange={e => updateFirebaseConfig({...firebaseConfig, projectId: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none" /></div>
                        <div className="space-y-2"><label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">API Key</label><input type="password" placeholder="AIzaSy..." value={firebaseConfig.apiKey} onChange={e => updateFirebaseConfig({...firebaseConfig, apiKey: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none" /></div>
                    </div>
                    <div className="pt-6 space-y-4">
                        <button onClick={() => updateFirebaseConfig({...firebaseConfig, isConnected: true})} className="w-full py-5 bg-amber-500 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-3"><Zap className="w-5 h-5" /> Hubungkan Cloud</button>
                        {firebaseConfig.isConnected && (
                            <button onClick={pushAllToFirebase} className="w-full py-5 bg-slate-900 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-2xl flex items-center justify-center gap-3 animate-in fade-in">
                                <CloudUpload className="w-5 h-5 text-emerald-400" /> Deploy local state to Cloud
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <div className="lg:col-span-5"><div className="bg-slate-900 p-12 rounded-[4rem] text-white shadow-2xl space-y-8"><h4 className="text-xl font-black uppercase tracking-tighter flex items-center gap-3"><Info className="w-6 h-6 text-emerald-400" /> Status Database</h4><div className="space-y-6"><div className="flex justify-between items-center pb-4 border-b border-white/10"><span className="text-slate-400 text-xs font-bold">Total Assets Lokal</span><span className="text-xl font-black text-white">{equipments.length}</span></div><div className="flex justify-between items-center pb-4 border-b border-white/10"><span className="text-slate-400 text-xs font-bold">Cloud Connection</span><span className={`px-4 py-1 rounded-full text-[9px] font-black uppercase ${firebaseConfig.isConnected ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>{firebaseConfig.isConnected ? 'Connected' : 'Disconnected'}</span></div></div></div></div>
        </div>
      )}

      {activeSubTab === 'database' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 animate-in slide-in-from-bottom-12 duration-500 pb-20">
            <div className="lg:col-span-8 space-y-10">
                <div className="bg-white p-12 rounded-[4rem] border border-indigo-100 shadow-sm relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none group-hover:scale-110 transition-transform duration-1000">
                        <Database className="w-64 h-64 text-indigo-500" />
                    </div>
                    
                    <div className="flex items-center gap-6 mb-12 relative z-10">
                        <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-[2rem] flex items-center justify-center shrink-0 shadow-inner">
                            <Database className="w-8 h-8" />
                        </div>
                        <div>
                            <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">Local Persistence Hub</h3>
                            <p className="text-slate-500 font-medium italic mt-1 leading-relaxed">Integrated LocalStorage management for secure safety data storage.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
                        {[
                            { label: 'Registered Assets', value: equipments.length, icon: <Layers className="w-4 h-4" /> },
                            { label: 'Schedule Plans', value: plans.length, icon: <ClipboardList className="w-4 h-4" /> },
                            { label: 'Actual Checks', value: actuals.length, icon: <CheckCircle2 className="w-4 h-4" /> },
                            { label: 'Findings Log', value: findings.length, icon: <AlertTriangle className="w-4 h-4" /> }
                        ].map(stat => (
                            <div key={stat.label} className="p-6 bg-slate-50 border border-slate-100 rounded-3xl hover:border-indigo-200 transition-all group">
                                <div className="text-slate-400 mb-3 group-hover:text-indigo-600 transition-colors">{stat.icon}</div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{stat.label}</p>
                                <p className="text-2xl font-black text-slate-900">{stat.value}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 pt-10 border-t border-slate-100 space-y-4 relative z-10">
                        <div className="flex flex-col md:flex-row gap-4">
                            <button 
                                onClick={() => {
                                    const data = { equipments, areaConfigs, plans, actuals, findings };
                                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `EHS_Database_Backup_${new Date().toISOString().split('T')[0]}.json`;
                                    a.click();
                                }}
                                className="flex-1 py-5 bg-white border-2 border-indigo-100 text-indigo-600 rounded-3xl font-black text-xs uppercase tracking-widest shadow-sm hover:bg-slate-50 flex items-center justify-center gap-3 transition-all"
                            >
                                <Database className="w-5 h-5" /> Export DB Backup (.json)
                            </button>
                            <button 
                                onClick={resetLocalDatabase}
                                className="flex-1 py-5 bg-rose-50 border-2 border-rose-100 text-rose-600 rounded-3xl font-black text-xs uppercase tracking-widest shadow-sm hover:bg-rose-100 flex items-center justify-center gap-3 transition-all"
                            >
                                <Trash2 className="w-5 h-5" /> Wipe & Reset Database
                            </button>
                        </div>
                    </div>
                </div>

                <div className="bg-indigo-950 p-10 rounded-[3rem] text-white shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                        <Zap className="w-32 h-32 text-amber-400" />
                    </div>
                    <div className="flex items-center gap-4 mb-6 relative z-10">
                        <Rocket className="w-8 h-8 text-amber-400" />
                        <h4 className="text-xl font-black uppercase tracking-tighter">Database Health Check</h4>
                    </div>
                    <div className="space-y-4 relative z-10">
                        <div className="flex items-center gap-4 p-5 bg-white/5 rounded-2xl border border-white/10">
                            <Check className="w-5 h-5 text-emerald-400" />
                            <div>
                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Consistency Engine</p>
                                <p className="text-xs font-bold text-white">Plan-Actual relationship mapping is active and consistent.</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 p-5 bg-white/5 rounded-2xl border border-white/10">
                            <Check className="w-5 h-5 text-emerald-400" />
                            <div>
                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Auto-Save Protocol</p>
                                <p className="text-xs font-bold text-white">Data mirrors in real-time to persistent local storage.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="lg:col-span-4 space-y-6">
                <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-3">
                        <Info className="w-5 h-5" /> Storage Info
                    </h4>
                    <div className="space-y-4">
                        <div className="p-5 bg-slate-50 rounded-2xl">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Local Provider</p>
                            <p className="text-sm font-black text-slate-900">Web Browser Storage</p>
                        </div>
                        <div className="p-5 bg-slate-50 rounded-2xl">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Encryption Mode</p>
                            <p className="text-sm font-black text-slate-900">Standard Client-Side</p>
                        </div>
                        <div className="p-5 bg-slate-50 rounded-2xl">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Relational Linkage</p>
                            <p className="text-sm font-black text-emerald-600">Active (Cascading Updates)</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* QR LABEL MODAL */}
      {showLabel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-sm animate-in fade-in no-print-overlay">
          <div className="bg-white rounded-[3rem] p-12 max-w-sm w-full text-center space-y-8 shadow-2xl scale-in">
            <div className="flex justify-between items-center border-b pb-6 no-print">
              <div className="text-left"><p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Safety Label</p><h4 className="text-lg font-black text-slate-900 uppercase tracking-tighter">Physical Asset Tag</h4></div>
              <button onClick={() => setShowLabel(null)} className="p-2 text-slate-300 hover:text-rose-500"><X className="w-6 h-6" /></button>
            </div>
            
            {/* AREA YANG AKAN DICETAK */}
            <div id="printable-label" className="bg-slate-50 p-10 rounded-[2.5rem] border-2 border-slate-200 border-dashed print:border-none print:bg-white print:p-0">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${showLabel.code}`} 
                className="w-full h-auto mx-auto rounded-xl shadow-lg print:shadow-none" 
                alt="qr-label" 
              />
              <div className="mt-8 space-y-2">
                <p className="text-xl font-black text-indigo-600 font-mono tracking-widest print:text-black">{showLabel.code}</p>
                <p className="text-xs font-black uppercase text-slate-500 print:text-black">{showLabel.type}</p>
                <p className="text-[10px] font-bold text-slate-400 italic hidden print:block text-center mt-4">Safety Master Inspection Hub</p>
              </div>
            </div>

            <button onClick={() => window.print()} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 no-print shadow-xl hover:bg-slate-800">
              <Printer className="w-4 h-4" /> Print Label
            </button>
          </div>
        </div>
      )}

      {/* MODAL FORM ASSET */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-xl animate-in fade-in duration-300 no-print">
            <div className="bg-white rounded-[4rem] w-full max-w-6xl shadow-2xl overflow-hidden scale-in">
                <form onSubmit={handleFormSubmit}>
                    <div className="p-10 border-b border-gray-100 flex justify-between items-center bg-slate-50">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-emerald-600 text-white rounded-2xl flex items-center justify-center shadow-lg">{editingEq ? <Edit2 className="w-6 h-6" /> : <Plus className="w-6 h-6" />}</div>
                            <div><h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">{editingEq ? 'Edit Asset Data' : 'Register New Asset'}</h3></div>
                        </div>
                        <button type="button" onClick={() => setShowForm(false)} className="p-4 text-slate-300 hover:text-rose-500 bg-white rounded-2xl shadow-sm transition-all"><X className="w-8 h-8" /></button>
                    </div>
                    <div className="p-12 grid grid-cols-1 lg:grid-cols-2 gap-16 max-h-[70vh] overflow-y-auto custom-scrollbar">
                        <div className="space-y-10">
                            <div className="bg-indigo-50 p-8 rounded-[2.5rem] border border-indigo-100 relative overflow-hidden">
                                <label className="text-[10px] font-black uppercase text-indigo-400 tracking-widest mb-4 block">Smart ID Code</label>
                                <input required type="text" value={formData.code} onChange={e => setFormData({...formData, code: e.target.value})} className="w-full bg-white border-2 border-indigo-200 rounded-2xl px-6 py-4 text-xl font-black text-indigo-600 font-mono outline-none" />
                            </div>
                            <div className="grid grid-cols-1 gap-6">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2"><label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Category</label><select value={formData.type} onChange={e => handleTypeChange(e.target.value as EquipmentType)} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm appearance-none cursor-pointer">{Object.values(EquipmentType).map(type => <option key={type} value={type}>{type}</option>)}</select></div>
                                  <div className="space-y-2"><label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Audit Area</label><select value={formData.area} onChange={e => setFormData({...formData, area: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm appearance-none cursor-pointer">{areaConfigs.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}</select></div>
                                </div>
                                <div className="space-y-2"><label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Section / Seksi</label><select required value={formData.section} onChange={e => setFormData({...formData, section: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm appearance-none cursor-pointer"><option value="">-- PILIH SEKSI --</option>{availableSections.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <h4 className="text-xs font-black uppercase text-slate-900 flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Checklist Teknis</h4>
                            <div className="bg-slate-50 p-8 rounded-[2.5rem] border border-slate-200 space-y-6">
                                <div className="flex gap-2"><input type="text" placeholder="Tambah Poin Inspeksi..." value={newChecklistItem} onChange={e => setNewChecklistItem(e.target.value)} onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())} className="flex-1 px-6 py-3 bg-white border border-slate-200 rounded-xl text-xs font-bold outline-none" /><button type="button" onClick={addChecklistItem} className="p-3 bg-slate-900 text-white rounded-xl"><Plus className="w-5 h-5" /></button></div>
                                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">{formData.technicalChecklist?.map((item, idx) => (<div key={idx} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl"><span className="text-xs font-bold text-slate-700">{item}</span><button type="button" onClick={() => removeChecklistItem(idx)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button></div>))}</div>
                            </div>
                        </div>
                    </div>
                    <div className="p-12 bg-slate-50 border-t border-slate-100 flex justify-end gap-6">
                        <button type="button" onClick={() => setShowForm(false)} className="px-10 py-5 bg-white border border-slate-200 text-slate-500 rounded-3xl font-black text-xs uppercase tracking-widest">Batal</button>
                        <button type="submit" className="px-16 py-5 bg-slate-900 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-2xl">Simpan Aset</button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default MasterData;
